<script setup lang="ts">
import { computed, reactive, watch } from 'vue';
// 只引密码规则这一个小模块(整个共享包进首屏主包太大)
import { PASSWORD_RULE_TEXT, passwordProblem } from '@cg/shared/password';
import { useSession } from '../stores/session.js';
import { toast, toastError } from '../ui/toast.js';
import Modal from './Modal.vue';

/**
 * 修改 / 设置密码(原型 global/login「后台账号首次登录须修改密码」+ 后台「账号安全」):
 * 有密码的输旧密码;一直用验证码登录、没设过密码的用本人手机验证码。改完其他设备下线。
 * force = 初始密码必须先改(不能关)。
 */
const props = defineProps<{ open: boolean; force?: boolean }>();
const emit = defineEmits<{ (e: 'close'): void }>();
const session = useSession();
const hasPassword = computed(() => session.me?.hasPassword !== false);
const f = reactive({ old: '', code: '', pwd: '', pwd2: '', busy: false, countdown: 0, hint: '' });
watch(
  () => props.open,
  (v) => {
    if (v) Object.assign(f, { old: '', code: '', pwd: '', pwd2: '', busy: false, hint: '' });
  },
);
const problem = computed(() => (f.pwd ? passwordProblem(f.pwd) : null));
const mismatch = computed(() => !!f.pwd2 && f.pwd2 !== f.pwd);
const ready = computed(
  () =>
    !f.busy &&
    !!f.pwd &&
    !problem.value &&
    f.pwd === f.pwd2 &&
    (hasPassword.value ? !!f.old : /^\d{6}$/.test(f.code)),
);

let timer: ReturnType<typeof setInterval> | undefined;
async function sendCode() {
  try {
    const r = await session.requestOwnPasswordCode();
    f.countdown = r.resendAfterSec;
    if (r.devCode) {
      f.code = r.devCode;
      f.hint = `开发环境:验证码 ${r.devCode} 已自动填入`;
    }
    clearInterval(timer);
    timer = setInterval(() => {
      f.countdown -= 1;
      if (f.countdown <= 0) clearInterval(timer);
    }, 1000);
  } catch (e) {
    toastError(e);
  }
}

async function save() {
  if (!ready.value) return;
  f.busy = true;
  try {
    await session.changePassword(
      hasPassword.value ? { oldPassword: f.old, newPassword: f.pwd } : { code: f.code, newPassword: f.pwd },
    );
    await session.loadMe().catch(() => undefined);
    toast('密码已修改;其他设备上的登录已全部下线', 'ok', 3600);
    emit('close');
  } catch (e) {
    toastError(e);
  } finally {
    f.busy = false;
  }
}
async function logout() {
  await session.logout();
  location.href = '/login';
}
</script>

<template>
  <Modal
    :open="open"
    :title="force ? '请先修改密码' : hasPassword ? '修改密码' : '设置登录密码'"
    :width="480"
  >
    <p v-if="force" class="note warn xs">
      你还在用开号时给的初始密码。为保护机构数据,后台账号第一次登录必须先改成自己的密码。
    </p>
    <p v-else-if="!hasPassword" class="small muted">
      你一直用验证码登录,还没有设过密码。设了以后也可以用手机号 + 密码登录(新设备会要短信二次验证)。
    </p>
    <div v-if="hasPassword" class="field mt8">
      <label>当前密码</label>
      <input v-model="f.old" class="input" type="password" autocomplete="current-password" maxlength="64" />
    </div>
    <div v-else class="field mt8">
      <label>手机验证码</label>
      <div class="row gap8">
        <input
          v-model.trim="f.code"
          class="input"
          inputmode="numeric"
          maxlength="6"
          placeholder="6 位验证码"
        />
        <button type="button" class="btn soft cp-send" :disabled="f.countdown > 0" @click="sendCode">
          {{ f.countdown > 0 ? `${f.countdown}s` : '发送验证码' }}
        </button>
      </div>
      <div v-if="f.hint" class="help">{{ f.hint }}</div>
    </div>
    <div class="field">
      <label>新密码</label>
      <input v-model="f.pwd" class="input" type="password" autocomplete="new-password" maxlength="20" />
      <div class="help" :class="{ 'cp-bad': problem }">
        {{ problem ?? `${PASSWORD_RULE_TEXT};不能与最近 3 次相同` }}
      </div>
    </div>
    <div class="field">
      <label>再输一次</label>
      <input v-model="f.pwd2" class="input" type="password" autocomplete="new-password" maxlength="20" />
      <div v-if="mismatch" class="help cp-bad">两次输入不一样</div>
    </div>
    <template #footer>
      <button v-if="force" class="btn" @click="logout">退出登录</button>
      <button v-else class="btn" @click="emit('close')">取消</button>
      <button class="btn primary" :disabled="!ready" @click="save">
        {{ f.busy ? '保存中…' : '保存新密码' }}
      </button>
    </template>
  </Modal>
</template>

<style scoped>
.cp-send {
  flex: none;
  height: 42px;
}
.cp-bad {
  color: var(--bad);
}
</style>
